/**
 * Вкладка «Типы заявок» в Настройках (миграция 0068, правка Артёма
 * «нужна настройка типов заявок для менеджера»).
 *
 * Тип заявки = категория услуг с is_request_type=true. Менеджер видит список
 * настроенных типов (Уборка, Поломка, Room service, Кальян, Трансфер,
 * Экскурсии…), может добавить свой (название + опц. иконка + опц. отдел-
 * исполнитель), переименовать/сменить иконку/отдел и выключить (деактивировать).
 *
 * Создание/правка — ОТДЕЛЬНЫЙ экран (форма вместо списка) с кнопками снизу
 * «Сохранить»/«Назад» — feedback_every_form_is_a_separate_screen.
 *
 * RBAC: canManage — мутации только управляющим ролям (бэк гейтит
 * /v1/admin/service-catalog → manager/owner).
 *
 * Эти же типы заявок берёт форма «Новая заявка»
 * (GET /v1/service-catalog/request-types).
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { ServiceCategory, ServiceCategoryUpdateInput } from '@hsc/types';
import { DbIcon } from '../../shared/nav/DbIcon.js';
import { IconPicker } from './IconPicker.js';
import { iconMap, useIcons } from './useAmenities.js';
import {
  useCreateRequestType,
  useRequestTypeCategories,
  useSetRequestTypeActive,
  useUpdateRequestType,
} from './useRequestTypes.js';

/** Отделы-исполнители каталога (db/migrations/tenant/0050_departments_seed). */
const DEPARTMENTS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'guest_services', label: 'Гостевой сервис' },
  { code: 'housekeeping', label: 'Хаускипинг' },
  { code: 'maintenance', label: 'Инженерная служба' },
  { code: 'f_and_b', label: 'Питание и напитки' },
  { code: 'front_desk', label: 'Ресепшн' },
  { code: 'spa', label: 'Спа и велнес' },
];

const DEPT_LABEL = new Map(DEPARTMENTS.map((d) => [d.code, d.label]));

/** Локализованное имя категории (LocalizedText → ru → первая доступная). */
function nameRu(c: ServiceCategory): string {
  return c.name.ru ?? c.name.en ?? Object.values(c.name)[0] ?? c.code;
}

/** Слаг из произвольного названия: транслит-минимум → ^[a-z][a-z0-9_]{2,30}$. */
function slugFromName(name: string): string {
  const map: Record<string, string> = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ё: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'y',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'c',
    ч: 'ch',
    ш: 'sh',
    щ: 'sch',
    ъ: '',
    ы: 'y',
    ь: '',
    э: 'e',
    ю: 'yu',
    я: 'ya',
  };
  let out = '';
  for (const ch of name.toLowerCase()) {
    if (ch in map) out += map[ch];
    else if (/[a-z0-9]/.test(ch)) out += ch;
    else out += '_';
  }
  out = out.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  if (out.length < 3) out = `req_${out}`;
  if (!/^[a-z]/.test(out)) out = `r_${out}`;
  return out.slice(0, 31);
}

export interface RequestTypesPanelProps {
  canManage?: boolean;
}

export function RequestTypesPanel({ canManage = false }: RequestTypesPanelProps): JSX.Element {
  const catsQ = useRequestTypeCategories();
  const iconsQ = useIcons();
  const icons = useMemo(() => iconMap(iconsQ.data), [iconsQ.data]);

  // null — список; '' — создание; id — правка.
  const [editing, setEditing] = useState<string | null>(null);

  const all = catsQ.data?.items ?? [];
  const requestTypes = useMemo(
    () =>
      [...all]
        .filter((c) => c.is_request_type)
        .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
    [all],
  );

  if (editing !== null) {
    return (
      <section data-testid="request-types-panel">
        <RequestTypeForm
          category={editing === '' ? undefined : all.find((c) => c.id === editing)}
          onDone={() => setEditing(null)}
        />
      </section>
    );
  }

  return (
    <section data-testid="request-types-panel">
      <p className="hsc-input__hint" style={{ marginBottom: 12 }}>
        Типы заявок — что персонал и гость могут заказать как заявку (Уборка, Поломка, Room service,
        Кальян, Трансфер, Экскурсии…). Они показываются в форме «Новая заявка».
      </p>

      {canManage && (
        <div className="amenity-toolbar">
          <Button onClick={() => setEditing('')} data-testid="request-type-add">
            + Добавить тип заявки
          </Button>
        </div>
      )}

      {catsQ.isLoading ? (
        <div className="fleet-grid" data-testid="request-types-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={56} radius={12} />
          ))}
        </div>
      ) : catsQ.isError ? (
        <Card>
          <p role="alert">Не удалось загрузить типы заявок. Попробуйте обновить страницу.</p>
        </Card>
      ) : requestTypes.length === 0 ? (
        <Card>
          <p>Типов заявок пока нет. {canManage && 'Добавьте первый кнопкой выше.'}</p>
        </Card>
      ) : (
        <ul className="amenity-list" data-testid="request-types-list">
          {requestTypes.map((c) => (
            <li
              key={c.id}
              className={`amenity-row${c.is_active ? '' : ' is-inactive'}`}
              data-testid={`request-type-${c.code}`}
            >
              <span className="amenity-row__icon">
                <DbIcon icon={c.icon ? icons.get(c.icon) : undefined} size={20} />
              </span>
              <span className="amenity-row__name">
                {nameRu(c)}
                {c.department_code && (
                  <span className="hsc-input__hint" style={{ marginLeft: 8 }}>
                    · {DEPT_LABEL.get(c.department_code) ?? c.department_code}
                  </span>
                )}
                {!c.is_active && (
                  <span className="hsc-input__hint" style={{ marginLeft: 8 }}>
                    · выключен
                  </span>
                )}
              </span>
              {canManage && (
                <span className="amenity-row__actions">
                  <Button
                    variant="ghost"
                    onClick={() => setEditing(c.id)}
                    data-testid={`request-type-${c.code}-edit`}
                  >
                    Изменить
                  </Button>
                  <ToggleActiveButton category={c} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ToggleActiveButton({ category }: { category: ServiceCategory }): JSX.Element {
  // Архивация идёт через выделенный POST /activate|/deactivate: бэк-DTO
  // категории НЕ содержит is_active, поэтому PATCH-флаг молча игнорируется (#77).
  const setActive = useSetRequestTypeActive();
  const next = !category.is_active;
  return (
    <Button
      variant="ghost"
      disabled={setActive.isPending}
      onClick={() => setActive.mutate({ id: category.id, active: next })}
      data-testid={`request-type-${category.code}-toggle`}
    >
      {setActive.isPending ? '…' : next ? 'Включить' : 'Выключить'}
    </Button>
  );
}

/** Полный набор изменяемых полей категории (PUT/PATCH = полная замена).
 *  Опциональные поля включаем только когда заданы (exactOptionalPropertyTypes). */
function fullPatch(c: ServiceCategory): ServiceCategoryUpdateInput {
  return {
    name: c.name,
    department_code: c.department_code,
    is_request_type: c.is_request_type,
    sort_order: c.sort_order,
    ...(c.description !== undefined ? { description: c.description } : {}),
    ...(c.workflow_template_id !== undefined
      ? { workflow_template_id: c.workflow_template_id }
      : {}),
    ...(c.sla_response_min !== undefined ? { sla_response_min: c.sla_response_min } : {}),
    ...(c.sla_resolution_min !== undefined ? { sla_resolution_min: c.sla_resolution_min } : {}),
    ...(c.icon !== undefined ? { icon: c.icon } : {}),
  };
}

function RequestTypeForm({
  category,
  onDone,
}: {
  category?: ServiceCategory | undefined;
  onDone: () => void;
}): JSX.Element {
  const isEdit = category !== undefined;
  const create = useCreateRequestType();
  const update = useUpdateRequestType();

  const [name, setName] = useState(category ? nameRu(category) : '');
  const [departmentCode, setDepartmentCode] = useState(
    category?.department_code ?? 'guest_services',
  );
  // Иконка категории хранится свободной строкой (ключ иконки) — пикер отдаёт
  // id иконки из /v1/icons; кладём его в category.icon.
  const [iconId, setIconId] = useState<string | null>(category?.icon ?? null);
  const [formError, setFormError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;
  const canSubmit = name.trim().length > 0 && !pending;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFormError(null);
    if (!canSubmit) return;

    if (isEdit && category) {
      update.mutate(
        {
          id: category.id,
          patch: {
            ...fullPatch(category),
            name: { ...category.name, ru: name.trim() },
            department_code: departmentCode,
            icon: iconId ?? '',
            is_request_type: true,
          },
        },
        {
          onSuccess: onDone,
          onError: (err) => setFormError(err.message || 'Не удалось сохранить тип заявки.'),
        },
      );
      return;
    }

    create.mutate(
      {
        code: slugFromName(name),
        name: { ru: name.trim() },
        department_code: departmentCode,
        icon: iconId ?? '',
        is_request_type: true,
      } as never,
      {
        onSuccess: onDone,
        onError: (err) =>
          setFormError(
            err.message ||
              'Не удалось создать тип заявки. Возможно, такой код уже занят — измените название.',
          ),
      },
    );
  }

  return (
    <Card data-testid="request-type-form">
      <div className="fleet-drawer__head">
        <h2 className="hsc-card__title">{isEdit ? 'Правка типа заявки' : 'Новый тип заявки'}</h2>
      </div>
      <form className="hsc-stack" onSubmit={handleSubmit}>
        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="rt-name">
            Название
          </label>
          <input
            id="rt-name"
            className="ta-input"
            data-testid="request-type-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, Замена полотенец"
            maxLength={64}
            required
          />
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="rt-department">
            Отдел-исполнитель
          </label>
          <select
            id="rt-department"
            className="ta-select"
            data-testid="request-type-department"
            value={departmentCode}
            onChange={(e) => setDepartmentCode(e.target.value)}
          >
            {DEPARTMENTS.map((d) => (
              <option key={d.code} value={d.code}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Иконка (опц.)</span>
          <IconPicker value={iconId} onChange={setIconId} />
        </div>

        {formError !== null && (
          <p role="alert" className="fleet-card__err" data-testid="request-type-form-error">
            {formError}
          </p>
        )}

        <div className="menu-form__submit">
          <Button type="submit" disabled={!canSubmit} data-testid="request-type-submit">
            {pending ? 'Сохраняем…' : isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onDone}
            data-testid="request-type-form-close"
          >
            Назад
          </Button>
        </div>
      </form>
    </Card>
  );
}
